import React, { useState, useEffect } from 'react';
import { useNavigate } from "react-router-dom";
import { useAuthenticator } from '@aws-amplify/ui-react';
import { useSelector, shallowEqual } from 'react-redux';
import { 
  Box, 
  IconButton, 
  Tooltip, 
  Divider, 
  Badge, 
  CircularProgress 
} from '@mui/material';
import { 
  ThumbUp as ThumbUpIcon,
  ThumbDown as ThumbDownIcon, 
  Comment as CommentIcon
} from '@mui/icons-material';
import { useSpReviewLikeMutation } from '../../../redux/thunks/sp/spreviewlike';

const ReviewInteractionSection = ({ review, businessName }) => {
  const navigate = useNavigate();
  const { authStatus } = useAuthenticator(context => [context.authStatus]);
  const token = useSelector((state) => state.auth?.token || null);
  const reviewLiked = useSelector((state) => state.auth?.user?.reviewsLiked || [], shallowEqual);
  const reviewDisliked = useSelector((state) => state.auth?.user?.reviewsDisliked || [], shallowEqual);
  const isAuthenticated = authStatus === 'authenticated' && token;

  const comments = review.comments || [];

  const [thumbsUpPressed, setThumbsUpPressed] = useState(reviewLiked.includes(review._id));
  const [thumbsDownPressed, setThumbsDownPressed] = useState(reviewDisliked.includes(review._id));
  const [likeReview, { isLoading: likeDislikeReviewLoading }] = useSpReviewLikeMutation();

  // Set initial state based on authentication and user preferences
  useEffect(() => {
    if (isAuthenticated) {
      setThumbsUpPressed(reviewLiked.includes(review._id));
      setThumbsDownPressed(reviewDisliked.includes(review._id));
    } else {
      // Reset state for unauthenticated users
      setThumbsUpPressed(false);
      setThumbsDownPressed(false);
    }
  }, [isAuthenticated, reviewLiked, reviewDisliked, review._id]);

  const handleCommentOnReview = () => {
    // Navigate to comments page with review data
    navigate(`/review/comments/${review._id}`, { state: {review, businessName} });
  };

  const handleLikeAction = async (actionType) => {
    if (!isAuthenticated) {
      // Navigate to sign-in with return path info
      navigate('/auth/signin', { 
        state: { 
          name: 'businessDetails', 
          link: `/business/${review.businessId}/${businessName}` 
        } 
      });
      return;
    }
    
    // Optimistically update UI state
    if (actionType === 'like') {
      setThumbsUpPressed(true);
      setThumbsDownPressed(false);
    } else if (actionType === 'dislike') {
      setThumbsDownPressed(true);
      setThumbsUpPressed(false);
    } else if (actionType === 'unlike') {
      setThumbsUpPressed(false);
    } else if (actionType === 'undislike') {
      setThumbsDownPressed(false);
    }

    // Create payload and send API request
    const payload = {
      auth: {
        token: token,
        authType: 'token'
      },
      reviewId: review._id,
      action: actionType
    };

    try {
      await likeReview({ payload });
    } catch (error) {
      console.error('Error performing like action:', error);
      // Revert optimistic UI update in case of error
      if (actionType === 'like' || actionType === 'unlike') {
        setThumbsUpPressed(reviewLiked.includes(review._id));
      } else if (actionType === 'dislike' || actionType === 'undislike') {
        setThumbsDownPressed(reviewDisliked.includes(review._id));
      }
    }
  };

  const handleThumbsUpPress = () => {
    if (!isAuthenticated) {
      // Navigate to sign-in with return path info
      navigate('/auth/signin', { 
        state: { 
          name: 'businessDetails', 
          link: `/business/${review.businessId}/${businessName}` 
        } 
      });
      return;
    }
    
    if (thumbsUpPressed) {
      handleLikeAction('unlike');
    } else {
      handleLikeAction('like');
      if (thumbsDownPressed) {
        handleLikeAction('undislike');
      }
    }
  };

  const handleThumbsDownPress = () => {
    if (!isAuthenticated) {
      // Navigate to sign-in with return path info
      navigate('/auth/signin', { 
        state: { 
          name: 'businessDetails', 
          link: `/business/${review.businessId}/${businessName}` 
        } 
      });
      return;
    }
    
    if (thumbsDownPressed) {
      handleLikeAction('undislike');
    } else {
      handleLikeAction('dislike');
      if (thumbsUpPressed) {
        handleLikeAction('unlike');
      }
    }
  };

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Tooltip title={isAuthenticated ? (thumbsUpPressed ? "Remove Like" : "Like") : "Sign in to like"}>
            <span>
              <IconButton 
                color={thumbsUpPressed ? 'primary' : 'default'} 
                aria-label="Thumb Up" 
                onClick={handleThumbsUpPress}
                disabled={likeDislikeReviewLoading}
              >
                <ThumbUpIcon />
              </IconButton>
            </span>
          </Tooltip>
          
          <Tooltip title={isAuthenticated ? (thumbsDownPressed ? "Remove Dislike" : "Dislike") : "Sign in to dislike"}>
            <span>
              <IconButton 
                color={thumbsDownPressed ? 'error' : 'default'} 
                aria-label="Thumb Down" 
                onClick={handleThumbsDownPress}
                disabled={likeDislikeReviewLoading}
              >
                <ThumbDownIcon />
              </IconButton>
            </span>
          </Tooltip>
          
          <Tooltip title={isAuthenticated ? "Comment on review" : "Sign in to comment"}>
            <span>
              <IconButton
                onClick={handleCommentOnReview}
              >
                <Badge badgeContent={comments.length} color="error">
                  <CommentIcon />
                </Badge>
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>
      <Divider sx={{ my: 2 }} />
    </>
  );
};

export default ReviewInteractionSection;